import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import authService from "../services/authService";
import vercelAnalytics from "../services/vercelAnalyticsService";
import "./Register.css";

const Register = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordMessage, setPasswordMessage] = useState("");
  const navigate = useNavigate();

  // Efeito para animação de entrada
  useEffect(() => {
    setIsVisible(true);

    // Track registration form opened
    vercelAnalytics.trackRegistrationStarted('landing');

    // Verificar se o modo escuro está ativado
    const darkModeEnabled = document.body.classList.contains('dark-mode');
    setIsDarkMode(darkModeEnabled);
  }, []);

  // Função para alternar o modo escuro
  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
    document.body.classList.toggle("dark-mode", !isDarkMode);
  };

  // Função para verificar a força da senha
  const checkPasswordStrength = (password) => {
    let strength = 0;
    let message = "";

    if (password.length === 0) {
      setPasswordStrength(0);
      setPasswordMessage("");
      return;
    }

    // Verificar comprimento
    if (password.length >= 8) {
      strength += 1;
    }

    // Verificar letras maiúsculas e minúsculas
    if (password.match(/[a-z]/) && password.match(/[A-Z]/)) {
      strength += 1;
    }

    // Verificar números
    if (password.match(/\d/)) {
      strength += 1;
    }

    // Verificar caracteres especiais
    if (password.match(/[^a-zA-Z\d]/)) {
      strength += 1;
    }

    // Definir mensagem com base na força
    switch (strength) {
      case 0:
        message = "Muito fraca";
        break;
      case 1:
        message = "Fraca";
        break;
      case 2:
        message = "Média";
        break;
      case 3:
        message = "Forte";
        break;
      case 4:
        message = "Muito forte";
        break;
      default:
        message = "";
    }

    setPasswordStrength(strength);
    setPasswordMessage(message);
  };

  // Função para lidar com a mudança de senha
  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    checkPasswordStrength(newPassword);
  };

  // Função auxiliar para validar dados
  const validateFormData = () => {
    if (password !== confirmPassword) {
      return "As senhas não coincidem.";
    }
    if (!email || !password) {
      return "Email e senha são obrigatórios.";
    }
    if (password.length < 6) {
      return "A senha deve ter pelo menos 6 caracteres.";
    }
    return null;
  };

  // Removida função de teste de conectividade - agora será feita automaticamente pelo authService

  // Função auxiliar para fazer o registro usando authService
  const performRegistration = async () => {
    const startTime = Date.now();
    console.log(`⏱️ Iniciando requisição: ${new Date().toISOString()}`);

    const result = await authService.register({
      name: name.trim(),
      email: email.trim(),
      password: password
    });

    const duration = Date.now() - startTime;
    console.log(`⏱️ Registro concluído em ${duration}ms`);
    
    return result;
  };

  // Função auxiliar para tratar erros
  const handleError = (err) => {
    if (process.env.NODE_ENV === 'development') {
      console.error("Erro no registro:", err);
    }

    if (err.code === 'ECONNABORTED') {
      return "A operação demorou muito tempo. Por favor, tente novamente mais tarde.";
    }

    if (err.response) {
      const { status, data } = err.response;
      
      if (status === 504) {
        return "O servidor está demorando muito para responder. Por favor, tente novamente mais tarde ou use o formulário de contato para solicitar um registro manual.";
      }
      if (status >= 500) {
        return "Erro interno do servidor. Nossa equipe foi notificada. Tente novamente em alguns minutos.";
      }
      if (status === 409) {
        return "Um usuário com este e-mail já está registrado. Tente fazer login ou use um e-mail diferente.";
      }
      if (status === 400) {
        return data?.message || "Dados inválidos. Verifique os campos e tente novamente.";
      }
      
      return data?.message || data?.error || "Erro do servidor. Tente novamente.";
    }

    if (err.request) {
      return "Não foi possível conectar ao servidor. Verifique sua conexão com a internet e tente novamente.";
    }

    return "Erro na configuração da requisição. Tente recarregar a página.";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const validationError = validateFormData();
      if (validationError) {
        setError(validationError);
        return;
      }

      await performRegistration();

      // Track successful registration
      vercelAnalytics.trackRegistrationCompleted('individual');
      vercelAnalytics.trackMainFunnel.discovery('registration_page');

      setSuccess(true);
      setTimeout(() => navigate("/login"), 2000);

    } catch (err) {
      const errorMessage = handleError(err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`register-page ${isDarkMode ? "dark" : ""} ${isVisible ? "visible" : ""}`}>
      <div className="register-header">
        <Link to="/" className="logo">
          <span className="logo-icon">❤️</span>
          <span className="logo-text">Casal Resolve</span>
        </Link>
        <button onClick={toggleDarkMode} className="theme-toggle">
          {isDarkMode ? "☀️" : "🌙"}
        </button>
      </div>

      <div className="register-container">
        <div className="register-box">
          <div className="register-content">
            <h1 className="register-title">Crie sua conta</h1>
            <p className="register-subtitle">Junte-se a milhares de casais que estão melhorando seus relacionamentos.</p>

            {error && (
              <div className="error-message">
                <p>{error}</p>
                {(error.includes("demorando muito") || error.includes("temporariamente indisponível")) && (
                  <p className="alternative-option">
                    Estamos enfrentando problemas técnicos com o registro automático.
                    Por favor, <a href="mailto:contato@casalresolve.com.br?subject=Solicitação de Registro&body=Olá, gostaria de solicitar um registro manual para o email: ">entre em contato</a> para solicitar um registro manual.
                  </p>
                )}
              </div>
            )}

            {success && (
              <div className="success-message">
                <span className="success-icon">✓</span>
                <p>Registro realizado com sucesso! Redirecionando para o login...</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="register-form">
              <div className="form-group">
                <label htmlFor="name">Nome completo</label>
                <div className="input-wrapper">
                  <span className="input-icon">👤</span>
                  <input
                    type="text"
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Digite seu nome completo"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="email">E-mail</label>
                <div className="input-wrapper">
                  <span className="input-icon">✉️</span>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Digite seu e-mail"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="password">Senha</label>
                <div className="input-wrapper">
                  <span className="input-icon">🔒</span>
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={handlePasswordChange}
                    placeholder="Crie uma senha forte"
                    minLength="6"
                    required
                  />
                </div>
                {password && (
                  <div className="password-strength">
                    <div className="strength-meter">
                      <div
                        className={`strength-meter-fill strength-${passwordStrength}`}
                        style={{ width: `${passwordStrength * 25}%` }}
                      ></div>
                    </div>
                    <span className={`strength-text strength-${passwordStrength}`}>
                      {passwordMessage}
                    </span>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirme sua senha</label>
                <div className="input-wrapper">
                  <span className="input-icon">🔐</span>
                  <input
                    type="password"
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Digite sua senha novamente"
                    minLength="6"
                    required
                  />
                </div>
                {confirmPassword && password !== confirmPassword && (
                  <div className="password-mismatch">As senhas não coincidem</div>
                )}
              </div>

              <div className="terms-privacy">
                <p>
                  Ao se registrar, você concorda com nossos <a href="#">Termos de Uso</a> e <a href="#">Política de Privacidade</a>.
                </p>
              </div>

              <button
                type="submit"
                className="register-button"
                disabled={loading || password !== confirmPassword}
              >
                {loading ? (
                  <span className="loading-spinner">
                    <span className="spinner"></span>
                    Processando...
                  </span>
                ) : (
                  "Criar conta"
                )}
              </button>
            </form>

            <div className="alternative-registration">
              <p>Problemas com o registro?</p>
              <a href="mailto:contato@casalresolve.com.br?subject=Solicitação de Registro&body=Olá, gostaria de solicitar um registro manual para o email: ">
                Solicite um registro manual
              </a>
            </div>

            <div className="login-link">
              <p>Já tem uma conta?</p>
              <Link to="/login" className="login-button-link">Faça login</Link>
            </div>
          </div>

          <div className="register-decoration">
            <div className="decoration-content">
              <h2>Comece sua jornada para um relacionamento mais saudável</h2>
              <p>O Casal Resolve oferece ferramentas para melhorar a comunicação e resolver conflitos de forma construtiva.</p>
              <div className="decoration-image"></div>
              <div className="decoration-features">
                <div className="feature">
                  <span className="feature-icon">🔒</span>
                  <span>Privacidade garantida</span>
                </div>
                <div className="feature">
                  <span className="feature-icon">🚀</span>
                  <span>Resultados rápidos</span>
                </div>
                <div className="feature">
                  <span className="feature-icon">💖</span>
                  <span>Relacionamento fortalecido</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <footer className="register-footer">
        <p>&copy; 2025 Casal Resolve - Plataforma para resolução de conflitos</p>
      </footer>
    </div>
  );
};

export default Register;
